import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import { useMenu } from '@hooks/useMenu';
import useMessage from '@hooks/useMessage';
import { useModalWindow } from '@hooks/useModalWindow';
import { EnumSortDirection } from '@hooks/useRequest';
import { IDespesa, useDespesaStatusCombo } from '@model/Despesa';
import { useEmpresaCombo } from '@model/Empresa';
import { IDespesaFilter, useDespesaService } from '@services/DespesaService';
import React from 'react';
import { DespesaFilter } from './DespesaFilter';
import { DespesaMenu } from './DespesaMenu';
import { DespesaRegistrarModal } from './DespesaRegistrarModal';
import { DespesaRegistrarReciboModal } from './DespesaRegistrarReciboModal';
import { DespesaSummary } from './DespesaSummary';
import { DespesaTable } from './DespesaTable';

export type DespesaProps = {
}

export function Despesa (props: DespesaProps) {
    const data = useData<IDespesa, IDespesaFilter>({
        filter: {
            despesaId: useField(),
            empresaId: useField(),
            descricao: useField(),
            competenciaReferencia: useField(),
            status: useField()
        },

        sortField: {
            name: "despesaId",
            direction: EnumSortDirection.Desc
        }
    })

    const loading = useLoading();
    const message = useMessage();
    const despesaService = useDespesaService();
    const menu = useMenu();
    const despesaStatusCombo = useDespesaStatusCombo();
    const empresaCombo = useEmpresaCombo();

    const despesaRegistrarModal = useModalWindow();
    const despesaRegistrarReciboModal = useModalWindow();

    React.useEffect(() => {
        if (!loading.isLoading) {
            loading.start();
            despesaService.listar(data.requestedData)
                .then(data.handleResultResponse)
                .catch(message.showException)
                .finally(loading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        despesaStatusCombo.loadOptions();
        empresaCombo.loadOptions();
    })

    return (
        <Main>
            <PageTitle>
                <HorizontalGrid>
                    <GridItem>
                        <PageTitleText text="Despesas" />
                    </GridItem>
                    <GridItem floatRight>
                        <HorizontalGrid spacing={1}>
                            <GridItem>
                                <PrimaryButton onClick={despesaRegistrarReciboModal.open}>Novo Recibo</PrimaryButton>
                            </GridItem>
                            
                            <GridItem>
                                <PrimaryButton onClick={despesaRegistrarModal.open}>Nova Despesa</PrimaryButton>                                
                            </GridItem>
                        </HorizontalGrid>
                    </GridItem>
                </HorizontalGrid>
            </PageTitle>

            <PageContent>
                <DespesaFilter
                    filter={data.filter}
                    isLoading={loading.isLoading}
                    empresaCombo={empresaCombo}
                    despesaStatusCombo={despesaStatusCombo} 
                />
                
                <DespesaSummary 
                    requestedData={data.requestedData}
                />

                <DespesaTable 
                    data={data}
                    isLoading={loading.isLoading}
                    menu={menu}
                />
            </PageContent>

            {
                data.focusedRow !== undefined && (
                    <React.Fragment>
                        <DespesaMenu 
                            menu={menu}
                            commandLoading={loading}
                            onDespesaAtualizada={data.handleUpdateFocusedRow}
                            despesa={data.focusedRow}
                        />
                    </React.Fragment>
                )
            }

            <DespesaRegistrarModal 
                modal={despesaRegistrarModal}
                onDespesaRegistrada={data.filter.handleFilterRequest}
                empresaCombo={empresaCombo}
                despesaStatusCombo={despesaStatusCombo} 
            />

            <DespesaRegistrarReciboModal 
                modal={despesaRegistrarReciboModal}
                onDespesaRegistrada={data.filter.handleFilterRequest}
                empresaCombo={empresaCombo}
                despesaStatusCombo={despesaStatusCombo} 
            />
        </Main>
    )
}